import { MoreVert as MoreVertIcon } from 'components/icons';
import { CloseCaseDropdownItem, useMayOfferCloseCase } from 'pages/matters/closing/CloseCaseMenuItem';
import { MatterVisibilityInfo } from 'types/matters';
import { cn } from 'utils/cn';
import { TableRowCallForActionButton } from 'components/buttons/CallForActionButton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { Tooltip } from 'components/ui/tooltip';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useExpenseEntryDisabled } from 'utils/useExpenseEntryDisabled';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';

type MenuItemType = {
    label: string;
    onClick: () => void;
    isDisabled?: boolean;
    disabledTooltip?: string;
};

type Props = {
    matter: {
        id: number;
        name: string;
        fullName: string;
        isNonBillable: boolean;
        matterStateTypeCode: string;
    } & MatterVisibilityInfo;
    isDisabled?: boolean;
    hasLimitedVisibility?: boolean;
};

export const MatterActionButtons = ({ matter, isDisabled, hasLimitedVisibility = false }: Props): JSX.Element => {
    const hasPermissionCreateTimecards = useHasPermissions(['CREATE.TIMECARDS']);
    const hasPermissionCreateExpenses = useHasPermissions(['CREATE.EXPENSES']);
    const hasPermissionCreateMatters = useHasPermissions(['CREATE.MATTERS', 'CREATE.MATTERS.CLIENTS']);
    const isExpenseEntryDisabled = useExpenseEntryDisabled();
    const mayOfferCloseCase = useMayOfferCloseCase(matter.matterStateTypeCode);

    const navigateWithReturn = useNavigateWithReturnState();

    const handleAddExpense = () => {
        navigateWithReturn('/expense/new', {
            state: {
                presetMatter: {
                    id: matter.id,
                    name: matter.name,
                    fullName: matter.fullName,
                    isNonBillable: matter.isNonBillable,
                    isConfidential: matter.isConfidential,
                    visibility: matter.visibility,
                },
            },
        });
    };

    const handleAddTimecard = () => {
        navigateWithReturn(`/matter/${matter.id}/timecards/new`);
    };

    const handleCopyCase = () => {
        navigateWithReturn(`/matter/copy/${matter.id}`);
    };

    const menuItems: MenuItemType[] = [
        {
            label: t('matters.add-timecard'),
            onClick: handleAddTimecard,
            isDisabled: !hasPermissionCreateTimecards || isDisabled,
        },
        {
            label: t('matters.add-expense'),
            onClick: handleAddExpense,
            isDisabled: !hasPermissionCreateExpenses || isDisabled || isExpenseEntryDisabled,
            disabledTooltip: isExpenseEntryDisabled ? t('expenses.integration-entry-disabled') : undefined,
        },
        {
            label: t('matters.copy-case'),
            onClick: handleCopyCase,
            isDisabled: !hasPermissionCreateMatters || hasLimitedVisibility,
        },
    ];

    const hasActiveMenuItems =
        mayOfferCloseCase || menuItems.some((item) => !item.isDisabled || !!item.disabledTooltip);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={
                    <TableRowCallForActionButton
                        disabled={!hasActiveMenuItems}
                        shape="rounded"
                        aria-label={t('actions')}
                        className={cn(hasLimitedVisibility && 'bg-grey-200 hover:bg-grey-300')}
                    >
                        <MoreVertIcon />
                    </TableRowCallForActionButton>
                }
            />
            <DropdownMenuContent align="end" className="w-auto">
                {menuItems.map((item) =>
                    item.isDisabled && !item.disabledTooltip ? null : item.isDisabled && item.disabledTooltip ? (
                        // A disabled item takes no pointer events, so the tooltip hangs off a wrapper.
                        <Tooltip key={`menu-item-${item.label}`} content={item.disabledTooltip} side="left">
                            <div>
                                <DropdownMenuItem disabled>{item.label}</DropdownMenuItem>
                            </div>
                        </Tooltip>
                    ) : (
                        <DropdownMenuItem key={`menu-item-${item.label}`} onClick={item.onClick}>
                            {item.label}
                        </DropdownMenuItem>
                    )
                )}
                <CloseCaseDropdownItem matterId={matter.id} matterStateTypeCode={matter.matterStateTypeCode} />
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
